/**
 * Dr. Salus - Plans Page
 * Gerenciamento de planos de assinatura recorrente.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Package, Plus, Edit2, Loader2, AlertCircle,
  Check, X, RefreshCw, ToggleLeft, ToggleRight,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import drSalusService from '../../services/dr-salus/drSalusService';

const CYCLES = [
  { value: 'MONTHLY', label: 'Mensal' },
  { value: 'YEARLY', label: 'Anual' },
  { value: 'WEEKLY', label: 'Semanal' },
  { value: 'QUARTERLY', label: 'Trimestral' },
];

const BILLING_TYPES = [
  { value: 'UNDEFINED', label: 'Qualquer método' },
  { value: 'PIX', label: 'PIX' },
  { value: 'BOLETO', label: 'Boleto' },
  { value: 'CREDIT_CARD', label: 'Cartão de Crédito' },
];

const CHARGE_TYPES = [
  { value: 'RECURRENT', label: 'Assinatura recorrente' },
  { value: 'DETACHED', label: 'Cobrança única (acesso por N dias)' },
];

const EMPTY_FORM = {
  name: '', description: '', value: '', cycle: 'MONTHLY', billing_type: 'UNDEFINED',
  charge_type: 'RECURRENT', access_days: 30,
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://quora-backend-cl3.kinddesert-624c9557.brazilsouth.azurecontainerapps.io';

export default function PlansPage() {
  const { colors } = useTheme();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);

  // Desafio (order bump) — produto vendido de dentro do Dr. Salus
  const [desafio, setDesafio] = useState(null);
  const [desafioProduct, setDesafioProduct] = useState('');
  const [desafioSaving, setDesafioSaving] = useState(false);
  const [copied, setCopied] = useState(null);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await drSalusService.listPlans();
      setPlans(data.plans || []);
    } catch (e) {
      setError('Erro ao carregar planos.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDesafio = useCallback(async () => {
    try {
      const data = await drSalusService.getDesafioConfig();
      setDesafio(data);
      setDesafioProduct(data?.offer?.source === 'portal' ? (data.offer.product_id || '') : '');
    } catch (e) {
      setDesafio(null);
    }
  }, []);

  useEffect(() => { loadPlans(); loadDesafio(); }, [loadPlans, loadDesafio]);

  const openCreate = () => {
    setEditingPlan(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setForm({
      name: plan.name,
      description: plan.description || '',
      value: plan.value,
      cycle: plan.cycle || 'MONTHLY',
      billing_type: plan.billing_type,
      charge_type: plan.charge_type || 'RECURRENT',
      access_days: plan.access_days || 30,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.value) return;
    if (form.charge_type === 'DETACHED' && !(parseInt(form.access_days, 10) > 0)) {
      setError('Cobrança única precisa de dias de acesso.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        value: parseFloat(form.value),
        cycle: form.charge_type === 'RECURRENT' ? form.cycle : 'MONTHLY',
        billing_type: form.billing_type,
        charge_type: form.charge_type,
        access_days: form.charge_type === 'DETACHED' ? parseInt(form.access_days, 10) : null,
      };
      if (editingPlan) {
        await drSalusService.updatePlan(editingPlan.id, payload);
        setSuccessMsg('Plano atualizado!');
      } else {
        await drSalusService.createPlan(payload);
        setSuccessMsg('Plano criado!');
      }
      setShowForm(false);
      await loadPlans();
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erro ao salvar plano.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDesafio = async () => {
    setDesafioSaving(true);
    setError(null);
    try {
      await drSalusService.setDesafioProduct(desafioProduct || null);
      await loadDesafio();
      setSuccessMsg('Produto do Desafio atualizado!');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erro ao salvar o produto do Desafio.');
    } finally {
      setDesafioSaving(false);
    }
  };

  const copy = (text, key) => {
    try { navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(null), 1500); } catch (e) { /* noop */ }
  };

  const planTypeLabel = (plan) => (
    (plan.charge_type || 'RECURRENT') === 'DETACHED'
      ? `Cobrança única · ${plan.access_days} dias de acesso`
      : `Assinatura · ${cycleLabel(plan.cycle)}`
  );

  const landingUrl = `${window.location.origin}/landing/dr-salus.html`;
  const landingDesafioUrl = `${window.location.origin}/landing/desafio.html`;

  const toggleActive = async (plan) => {
    try {
      await drSalusService.updatePlan(plan.id, { active: !plan.active });
      await loadPlans();
    } catch (e) {
      setError('Erro ao atualizar status.');
    }
  };

  const cycleLabel = (c) => CYCLES.find(x => x.value === c)?.label || c;

  const s = {
    page: { padding: '24px', maxWidth: '900px' },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' },
    title: { fontSize: '20px', fontWeight: 700, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: '10px' },
    btn: (variant = 'primary') => ({
      display: 'flex', alignItems: 'center', gap: '6px',
      padding: '8px 16px', borderRadius: '8px', border: 'none',
      cursor: 'pointer', fontSize: '13px', fontWeight: 600,
      backgroundColor: variant === 'primary' ? '#10b981' : colors.bgSecondary,
      color: variant === 'primary' ? '#fff' : colors.textPrimary,
    }),
    card: {
      backgroundColor: colors.bgSecondary, border: `1px solid ${colors.borderColor}`,
      borderRadius: '12px', padding: '20px', marginBottom: '12px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    },
    planName: { fontSize: '15px', fontWeight: 600, color: colors.textPrimary },
    planMeta: { fontSize: '12px', color: colors.textSecondary, marginTop: '4px' },
    planValue: { fontSize: '20px', fontWeight: 700, color: '#10b981' },
    modal: {
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    },
    modalBox: {
      backgroundColor: colors.bgPrimary, borderRadius: '16px',
      padding: '28px', width: '440px', maxWidth: '95vw',
    },
    label: { fontSize: '12px', fontWeight: 600, color: colors.textSecondary, marginBottom: '6px', display: 'block' },
    input: {
      width: '100%', padding: '10px 12px', borderRadius: '8px',
      border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
      color: colors.textPrimary, fontSize: '14px', boxSizing: 'border-box',
    },
    select: {
      width: '100%', padding: '10px 12px', borderRadius: '8px',
      border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
      color: colors.textPrimary, fontSize: '14px',
    },
    formRow: { marginBottom: '16px' },
    row: { display: 'flex', gap: '12px', marginBottom: '16px' },
    actions: { display: 'flex', gap: '8px' },
    iconBtn: {
      padding: '6px 8px', borderRadius: '6px', border: 'none',
      cursor: 'pointer', backgroundColor: 'transparent', color: colors.textSecondary,
    },
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>
          <Package size={22} color="#10b981" />
          Planos Dr. Salus
        </h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={s.btn('secondary')} onClick={loadPlans}>
            <RefreshCw size={14} /> Atualizar
          </button>
          <button style={s.btn('primary')} onClick={openCreate}>
            <Plus size={14} /> Novo Plano
          </button>
        </div>
      </div>

      {successMsg && (
        <div style={{ padding: '10px 16px', backgroundColor: '#10b98120', color: '#10b981', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Check size={16} /> {successMsg}
        </div>
      )}

      {error && (
        <div style={{ padding: '10px 16px', backgroundColor: '#ef444420', color: '#ef4444', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          <Loader2 size={24} color="#10b981" style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      ) : plans.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px', color: colors.textSecondary }}>
          Nenhum plano criado. Crie o primeiro plano para começar.
        </div>
      ) : (
        plans.map(plan => (
          <div key={plan.id} style={{ ...s.card, opacity: plan.active ? 1 : 0.5 }}>
            <div>
              <div style={s.planName}>{plan.name}</div>
              {plan.description && <div style={s.planMeta}>{plan.description}</div>}
              <div style={s.planMeta}>{planTypeLabel(plan)} · {plan.billing_type === 'UNDEFINED' ? 'Qualquer método' : plan.billing_type}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={s.planValue}>R$ {parseFloat(plan.value).toFixed(2)}</div>
              <div style={s.actions}>
                <button style={s.iconBtn} onClick={() => openEdit(plan)} title="Editar">
                  <Edit2 size={15} />
                </button>
                <button style={{ ...s.iconBtn, color: plan.active ? '#10b981' : colors.textSecondary }} onClick={() => toggleActive(plan)} title={plan.active ? 'Desativar' : 'Ativar'}>
                  {plan.active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                </button>
              </div>
            </div>
          </div>
        ))
      )}

      {/* ===== Checkout público / landing ===== */}
      <div style={{ ...s.card, display: 'block', marginTop: '20px' }}>
        <div style={s.planName}>Checkout público (landing page)</div>
        <div style={{ ...s.planMeta, lineHeight: 1.6 }}>
          Os planos <strong>ativos</strong> acima ficam disponíveis automaticamente na API pública e nas páginas prontas.
          Para integrar na landing do cliente, veja <code>backend/docs/INTEGRACAO_LANDING.md</code>.
        </div>
        {[
          ['Página pronta — Dr. Salus', landingUrl, 'l1'],
          ['Página pronta — Desafio', landingDesafioUrl, 'l2'],
          ['API — planos', `${API_BASE_URL}/api/public/dr-salus/plans`, 'a1'],
          ['API — checkout (POST)', `${API_BASE_URL}/api/public/dr-salus/checkout`, 'a2'],
          ['API — produtos do Desafio', `${API_BASE_URL}/api/public/desafios/products`, 'a3'],
        ].map(([label, url, key]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', fontSize: '12px' }}>
            <span style={{ color: colors.textSecondary, minWidth: '190px' }}>{label}</span>
            <code style={{ color: colors.textPrimary, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{url}</code>
            <button style={{ ...s.btn('secondary'), padding: '4px 8px', fontSize: '11px' }} onClick={() => copy(url, key)}>
              {copied === key ? 'Copiado!' : 'Copiar'}
            </button>
          </div>
        ))}
      </div>

      {/* ===== Desafio — order bump ===== */}
      <div style={{ ...s.card, display: 'block' }}>
        <div style={s.planName}>Desafio Meu Intestino Blindado · order bump</div>
        <div style={{ ...s.planMeta, lineHeight: 1.6 }}>
          Produto (da conta Desafios) que o Dr. Salus oferece na conversa quando faz sentido (queixa intestinal, objetivo de saúde intestinal ou pergunta direta).
          Quem paga é inscrito automaticamente no follow-up do produto — o mesmo caminho da venda direta.
        </div>
        {desafio ? (
          <>
            <div style={{ marginTop: '12px', fontSize: '13px', color: colors.textPrimary, lineHeight: 1.7 }}>
              <div><strong>Em vigor:</strong> {desafio.offer?.product?.name || '—'} {desafio.offer?.product?.value ? `· R$ ${parseFloat(desafio.offer.product.value).toFixed(2)}` : ''} <span style={{ color: colors.textSecondary }}>({desafio.offer?.source === 'portal' ? 'definido aqui' : 'padrão do sistema'})</span></div>
              <div><strong>Follow-up:</strong> {desafio.offer?.followup ? `${desafio.offer.followup.name} · ${desafio.offer.followup.status} · ${desafio.offer.followup.total_days} dias · ${desafio.offer.followup.recipients} participante(s)` : '—'}</div>
              <div><strong>Link de pagamento:</strong> {desafio.offer?.checkout_url ? <code>{desafio.offer.checkout_url}</code> : '—'}</div>
              <div><strong>Reoferta:</strong> no máximo 1 vez a cada {desafio.cooldown_days} dias por usuário · <strong>Número do Desafio:</strong> +{desafio.whatsapp_desafio}</div>
              {desafio.offer?.available ? (
                <div style={{ color: '#22c55e', marginTop: '4px' }}>✔ Oferta disponível para o agente</div>
              ) : (
                <div style={{ color: '#f59e0b', marginTop: '4px' }}>⚠ Oferta indisponível: {(desafio.offer?.problems || []).join('; ')}</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '12px' }}>
              <select style={{ ...s.select, width: 'auto', flex: 1 }} value={desafioProduct} onChange={e => setDesafioProduct(e.target.value)}>
                <option value="">Padrão do sistema ({desafio.offer?.product_id})</option>
                {(desafio.candidates?.products || []).map(p => (
                  <option key={p.id} value={p.id} disabled={!p.followup_id}>
                    {p.name} · R$ {parseFloat(p.value || 0).toFixed(2)}{p.sandbox ? ' · SANDBOX' : ''}{!p.followup_id ? ' · sem follow-up' : ''}{!p.active ? ' · inativo' : ''}
                  </option>
                ))}
              </select>
              <button style={s.btn('primary')} onClick={handleSaveDesafio} disabled={desafioSaving}>
                {desafioSaving ? <Loader2 size={14} /> : <Check size={14} />} Salvar
              </button>
            </div>
          </>
        ) : (
          <div style={{ ...s.planMeta, marginTop: '8px' }}>Carregando configuração do Desafio…</div>
        )}
      </div>

      {showForm && (
        <div style={s.modal}>
          <div style={s.modalBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '16px', fontWeight: 700, color: colors.textPrimary }}>
                {editingPlan ? 'Editar Plano' : 'Novo Plano'}
              </h2>
              <button style={s.iconBtn} onClick={() => setShowForm(false)}>
                <X size={18} />
              </button>
            </div>

            <div style={s.formRow}>
              <label style={s.label}>Nome do Plano *</label>
              <input style={s.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="ex: Plano Mensal Dr. Salus" />
            </div>

            <div style={s.formRow}>
              <label style={s.label}>Descrição</label>
              <input style={s.input} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Opcional" />
            </div>

            <div style={s.formRow}>
              <label style={s.label}>Tipo de cobrança</label>
              <select style={s.select} value={form.charge_type} onChange={e => setForm(f => ({ ...f, charge_type: e.target.value }))} disabled={!!editingPlan}>
                {CHARGE_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <div style={{ fontSize: '11px', color: colors.textSecondary, marginTop: '6px' }}>
                {form.charge_type === 'DETACHED'
                  ? 'A pessoa paga uma vez e o acesso ao Dr. Salus fica liberado pelos dias informados (expira sozinho depois da carência).'
                  : 'O Asaas cobra automaticamente a cada ciclo; inadimplência bloqueia o acesso após a carência.'}
              </div>
            </div>

            <div style={s.row}>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Valor (R$) *</label>
                <input style={s.input} type="number" min="0" step="0.01" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} placeholder="97.00" />
              </div>
              {form.charge_type === 'DETACHED' ? (
                <div style={{ flex: 1 }}>
                  <label style={s.label}>Dias de acesso *</label>
                  <input style={s.input} type="number" min="1" max="3650" value={form.access_days} onChange={e => setForm(f => ({ ...f, access_days: e.target.value }))} placeholder="90" />
                </div>
              ) : (
                <div style={{ flex: 1 }}>
                  <label style={s.label}>Ciclo</label>
                  <select style={s.select} value={form.cycle} onChange={e => setForm(f => ({ ...f, cycle: e.target.value }))}>
                    {CYCLES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
              )}
            </div>

            <div style={s.formRow}>
              <label style={s.label}>Método de Pagamento</label>
              <select style={s.select} value={form.billing_type} onChange={e => setForm(f => ({ ...f, billing_type: e.target.value }))}>
                {BILLING_TYPES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button style={s.btn('secondary')} onClick={() => setShowForm(false)} disabled={saving}>
                Cancelar
              </button>
              <button style={s.btn('primary')} onClick={handleSave} disabled={saving || !form.name || !form.value}>
                {saving ? <Loader2 size={14} /> : <Check size={14} />}
                {editingPlan ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
